import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { userRequest } from '../../utils/requestMethods';
import { useStateContext } from '../../contexts/ContextProvider';
import { MobileFriendlyNotification, SkeletonTable } from '../../components';
import DataTable from 'react-data-table-component';
import { FiArrowLeft, FiCheck, FiX, FiClock, FiUser, FiSmartphone, FiRefreshCw, FiFilter, FiEye, FiCalendar, FiAlertCircle } from 'react-icons/fi';
import { format } from 'date-fns';

const DeviceApprovalManagement = () => {
  const { schoolId } = useParams();
  const { school } = useStateContext();
  const [deviceRequests, setDeviceRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState({});
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [currentAction, setCurrentAction] = useState(null); // 'approve' or 'deny'
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [comment, setComment] = useState("");
  const [filters, setFilters] = useState({
    status: 'all',
    from: '',
    to: ''
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const statusOptions = [
    { value: 'all', label: 'All Requests', color: 'gray' },
    { value: 'pending', label: 'Pending', color: 'yellow' },
    { value: 'approved', label: 'Approved', color: 'green' },
    { value: 'denied', label: 'Denied', color: 'red' }
  ];

  const fetchDeviceRequests = async (customFilters = null) => {
    if (!schoolId) return;

    try {
      setLoading(true);
      const filterParams = customFilters || filters;
      
      let endpoint = `/device-approval/${schoolId}/requests?limit=100`;
      
      if (filterParams.status !== 'all') {
        endpoint += `&status=${filterParams.status}`;
      }
      if (filterParams.from) {
        endpoint += `&from=${encodeURIComponent(filterParams.from)}`;
      }
      if (filterParams.to) {
        endpoint += `&to=${encodeURIComponent(filterParams.to)}`;
      }

      const { data } = await userRequest.get(endpoint);
      const requestsData = data?.requests || data?.data || data || [];
      setDeviceRequests(Array.isArray(requestsData) ? requestsData : []);
    } catch (error) {
      console.error('Error fetching device requests:', error);
      setError(error.response?.data?.message || "Failed to fetch device requests.");
      setDeviceRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (schoolId) {
      fetchDeviceRequests();
    }
  }, [schoolId]);

  const handleApproveRequestClick = (requestId) => {
    setCurrentRequestId(requestId);
    setCurrentAction('approve');
    setComment("");
    setShowCommentModal(true);
  };

  const handleDenyRequestClick = (requestId) => {
    setCurrentRequestId(requestId);
    setCurrentAction('deny');
    setComment("");
    setShowCommentModal(true);
  };

  const handleConfirmAction = async () => {
    if (!currentRequestId || !currentAction) return;

    try {
      setProcessing(prev => ({ ...prev, [currentRequestId]: currentAction === 'approve' ? 'approving' : 'denying' }));
      
      const endpoint = `/device-approval/${schoolId}/requests/${currentRequestId}/${currentAction}`;
      await userRequest.post(endpoint, { comment: comment || "" });
      
      setSuccess(`Device request ${currentAction === 'approve' ? 'approved' : 'denied'} successfully!`);
      setShowCommentModal(false);
      setCurrentRequestId(null);
      setCurrentAction(null);
      setComment("");
      fetchDeviceRequests();
    } catch (error) {
      console.error(`Error ${currentAction === 'approve' ? 'approving' : 'denying'} request:`, error);
      setError(error.response?.data?.message || `Failed to ${currentAction} request.`);
    } finally {
      setProcessing(prev => ({ ...prev, [currentRequestId]: null }));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    fetchDeviceRequests(filters);
    setShowFilterModal(false);
  };

  const getStatusBadge = (status) => {
    const option = statusOptions.find(opt => opt.value === status);
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      default: 'bg-gray-100 text-gray-800'
    };
    
    return colors[status] || colors.default;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FiClock className="w-4 h-4 text-yellow-600" />;
      case 'approved':
        return <FiCheck className="w-4 h-4 text-green-600" />;
      case 'denied':
        return <FiX className="w-4 h-4 text-red-600" />;
      default:
        return <FiAlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const columns = [
    {
      name: "Teacher",
      selector: row => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-[#3F3DA5] rounded-full flex items-center justify-center">
            <FiUser className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-semibold text-gray-800">
              {row.user?.firstname} {row.user?.lastname}
            </div>
            <div className="text-sm text-gray-500">
              {row.user.email || 'No Email'}
            </div>
            {/*row.user?.email && (
              <div className="text-xs text-gray-400">
                {row.user.email}
              </div>
            )*/}
          </div>
        </div>
      ),
      sortable: true,
      width: "250px",
    },
    {
      name: "Device Info",
      selector: row => (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <FiSmartphone className="w-4 h-4 text-gray-500" />
            <span className="font-medium">{row.requested_device?.version || 'Unknown Device'}</span>
          </div>
          <div className="text-sm text-gray-500">
            OS: {row.requested_device?.os || 'Unknown OS'}
          </div>
          {/*<div className="text-xs text-gray-400">
            ID: {row.requested_device?.id || 'No Device ID'}
          </div>*/}
        </div>
      ),
      width: "200px",
    },
    {
      name: "Request Date",
      selector: row => (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <FiCalendar className="w-4 h-4 text-gray-500" />
            <span className="font-medium">
              {row.requested_at ? format(new Date(row.requested_at), 'dd/MM/yyyy') : (row.createdAt ? format(new Date(row.createdAt), 'dd/MM/yyyy') : '-')}
            </span>
          </div>
          <div className="text-sm text-gray-500">
            {row.requested_at ? format(new Date(row.requested_at), 'hh:mm a') : (row.createdAt ? format(new Date(row.createdAt), 'hh:mm a') : '-')}
          </div>
        </div>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "Status",
      selector: row => (
        <div className="flex items-center space-x-2">
          {getStatusIcon(row.status)}
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(row.status)}`}>
            {statusOptions.find(opt => opt.value === row.status)?.label || row.status}
          </span>
        </div>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "Reason",
      selector: row => (
        <div className="max-w-xs">
          <p className="text-sm text-gray-700 truncate" title={row.reason}>
            {row.reason || 'No reason provided'}
          </p>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-2">
          {row.status === 'pending' && (
            <>
              <button
                onClick={() => handleApproveRequestClick(row._id)}
                disabled={processing[row._id]}
                className="group p-2 text-green-600 bg-green-50 rounded-lg hover:bg-green-100 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Approve Request"
              >
                {processing[row._id] === 'approving' ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent"></div>
                ) : (
                  <FiCheck className="w-4 h-4 group-hover:scale-110 transition-transform" />
                )}
              </button>
              <button
                onClick={() => handleDenyRequestClick(row._id)}
                disabled={processing[row._id]}
                className="group p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Deny Request"
              >
                {processing[row._id] === 'denying' ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                ) : (
                  <FiX className="w-4 h-4 group-hover:scale-110 transition-transform" />
                )}
              </button>
            </>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      //width: "140px",
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: 'rgb(248, 250, 252)',
        fontWeight: "bold",
      },
    },
  };

  const getStatusCounts = () => {
    const counts = { pending: 0, approved: 0, denied: 0 };
    deviceRequests.forEach(request => {
      if (counts.hasOwnProperty(request.status)) {
        counts[request.status]++;
      }
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6">
          {/* Title and Info Section */}
          <div className="flex-1">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => window.history.back()}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Go Back"
              >
                <FiArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Device Approval Management</h1>
                <p className="text-gray-600 mt-1">Manage teacher device change requests</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center space-x-2 text-yellow-600 bg-yellow-50 px-4 py-2 rounded-lg">
                <FiClock className="w-5 h-5" />
                <span className="font-semibold">{statusCounts.pending} Pending</span>
              </div>
              <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
                <FiCheck className="w-5 h-5" />
                <span className="font-semibold">{statusCounts.approved} Approved</span>
              </div>
              <div className="flex items-center space-x-2 text-red-600 bg-red-50 px-4 py-2 rounded-lg">
                <FiX className="w-5 h-5" />
                <span className="font-semibold">{statusCounts.denied} Denied</span>
              </div>
              <div className="flex items-center space-x-2 text-[#3F3DA5] bg-blue-50 px-4 py-2 rounded-lg">
                <FiUser className="w-5 h-5" />
                <span className="font-semibold">{deviceRequests.length} Total</span>
              </div>
            </div>
          </div>

          {/* Action Buttons Section */}
          <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
            <button
              onClick={() => fetchDeviceRequests()}
              disabled={loading}
              className="group relative inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-[#3F3DA5] to-[#2E2D7A] hover:from-[#2E2D7A] hover:to-[#1F1E5C] focus:outline-none focus:ring-2 focus:ring-[#3F3DA5] focus:ring-offset-2 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-3"></div>
              ) : (
                <FiRefreshCw className="w-4 h-4 mr-3 group-hover:rotate-180 transition-transform duration-500" />
              )}
              <span className="font-semibold">{loading ? "Refreshing..." : "Refresh"}</span>
            </button>

            <button
              onClick={() => setShowFilterModal(true)}
              className="group relative inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <FiFilter className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform duration-200" />
              <span className="font-semibold">Filter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {success && (
        <MobileFriendlyNotification
          type="success"
          message={success}
          onClose={() => setSuccess("")}
          autoClose={3000}
        />
      )}
      {error && (
        <MobileFriendlyNotification
          type="error"
          message={error}
          onClose={() => setError("")}
          autoClose={5000}
        />
      )}

      {/* Device Requests Table */}
      <div className="bg-white rounded-2xl shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Device Change Requests</h2>
          <p className="text-sm text-gray-600 mt-1">
            Review and manage teacher device change requests
          </p>
        </div>
        <div className="overflow-x-auto">
          <DataTable
            data={Array.isArray(deviceRequests) ? deviceRequests : []}
            columns={columns}
            getRowId={(row) => row._id || row.id}
            pagination
            paginationPerPage={15}
            paginationRowsPerPageOptions={[15, 30, 50]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={6} />}
            highlightOnHover
            striped
            noDataComponent={
              <div className="p-8 text-center text-gray-500">
                <FiSmartphone className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No device requests found</p>
                <p className="text-sm text-gray-400 mt-2">Device requests will appear here when teachers submit them</p>
              </div>
            }
          />
        </div>
      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">Filter Requests</h2>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.from}
                  onChange={(e) => handleFilterChange('from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.to}
                  onChange={(e) => handleFilterChange('to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowFilterModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 text-sm font-medium text-white bg-[#3F3DA5] rounded-full hover:bg-[#2E2D7A]"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  currentAction === 'approve' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {currentAction === 'approve' ? (
                    <FiCheck className="w-5 h-5 text-green-600" />
                  ) : (
                    <FiX className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {currentAction === 'approve' ? 'Approve' : 'Deny'} Device Request
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">Add a comment (optional)</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowCommentModal(false);
                  setCurrentRequestId(null);
                  setCurrentAction(null);
                  setComment("");
                }}
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center transition-colors"
              >
                <FiX className="w-5 h-5" />
                <span className="sr-only">Close modal</span>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <label htmlFor="comment" className="block mb-2 text-sm font-semibold text-gray-900">
                  Comment <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="comment"
                  rows="4"
                  onChange={(e) => setComment(e.target.value)}
                  value={comment}
                  placeholder={`Enter a comment for ${currentAction === 'approve' ? 'approving' : 'denying'} this request...`}
                  className="bg-gray-50 border-2 border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full p-3.5 transition-all resize-none"
                />
                <p className="mt-1.5 text-xs text-gray-500">This comment will be associated with the {currentAction === 'approve' ? 'approval' : 'denial'} of the request</p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCommentModal(false);
                    setCurrentRequestId(null);
                    setCurrentAction(null);
                    setComment("");
                  }}
                  disabled={processing[currentRequestId]}
                  className="w-full sm:w-auto text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 font-semibold rounded-full text-sm px-6 py-2.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAction}
                  disabled={processing[currentRequestId]}
                  className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 text-white font-semibold rounded-full text-sm px-6 py-2.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                    currentAction === 'approve'
                      ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                      : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                  }`}
                >
                  {processing[currentRequestId] ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {currentAction === 'approve' ? 'Approving...' : 'Denying...'}
                    </>
                  ) : (
                    <>
                      {currentAction === 'approve' ? (
                        <>
                          <FiCheck className="w-4 h-4" />
                          Approve Request
                        </>
                      ) : (
                        <>
                          <FiX className="w-4 h-4" />
                          Deny Request
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceApprovalManagement;
